import firebase from "firebase/compat/app"

import {
  update,
  get,
  add,
  Doc,
  Collection,
  remove,
  ref,
  set,
  query,
  where
} from "typesaurus"

import { db, CollectionNames } from "../db"

import {
  collectionMap,
  TopicMap,
  TextType,
  AssetType,
  AssetRef,
  MetaType,
  SeqRef,
  BlockRef,
  InteractionType,
  LayoutType,
  SequenceType,
  MobileLayout,
  VideoSettings,
  SnapType,
  MediaType,
  LibraryAssetData,
  QtiInteractionType,
  Answer
} from "../db/types"

import cloneDeep from "lodash/cloneDeep"

import {
  Asset,
  Snap,
  SnapCourse,
  WordBank,
  SeqExercise,
  SeqScreen,
  SeqAssessment,
  SeqQtiScreen,
  SeqPassage,
  BlockText,
  BlockInteraction,
  BlockImage,
  BlockVideo,
  BlockPresentation,
  BlockPdf,
  Media
} from "./collections"

import {
  BlockInteractionSchema,
  BlockQtiInteractionSchema,
  AnswerSchema,
  BlockRefSchema,
  SeqScreenSchema,
  SeqRefSchema,
  SnapSchema,
  SeqExerciseSchema,
  BlockPdfSchema,
  BlockPresentationSchema,
  BlockTextSchema,
  AssetSchema,
  SnapCourseSchema,
  BlockImageSchema,
  BlockVideoSchema,
  LibrarySchema,
  MetaTypeSchema,
  SeqAssessmentSchema,
  SettingsSchema,
  SeqQtiScreenSchema,
  SeqPassageSchema
} from "./schemas"
import {
  Program,
  SnapCourseCreateInput,
  SnapCourseProgramCourseAssociationInput,
  SnapCourseType
} from "../../../types/gqlTypes/graphql"
import { storage } from "../firebase"
import { ScreenTypeToCollectionMapping } from "../authoring/pages/ScreenBank/constants"
import { ScreenBankEntityTypes } from "../authoring/pages/ScreenBank/types"
import { createSnapCourse } from "../../../services/ready-services/contentMgmnt/snapCourse/snapCourseMutationResolver"
import { InteractionTypes } from "../authoring/pages/QtiAuthoring/helpers/types"
import { INITIAL_RESPONSE_DECLARATION } from "../authoring/pages/QtiAuthoring/constants"
import { enqueueSnackbar } from "notistack"
import {
  updateScreenBankPassageConfig,
  updateScreenBankQuestionConfig
} from "../../../services/ready-services/contentMgmnt/itemsBank/itemsBankMutationResolvers"

const validationOptions = {
  strict: true,
  abortEarly: false,
  stripUnknown: true,
  recursive: true
}

// Create Meta for record
export const createMeta = (uid: string): MetaType => {
  return MetaTypeSchema.cast(
    {
      owner_id: uid,
      created: firebase.firestore.Timestamp.now(),
      updated: firebase.firestore.Timestamp.now(),
      deleted: false,
      last_updated_by: uid
    },
    validationOptions
  ) as MetaType
}

// Get GCS path for asset
export const getAssetLocation = (assetRef: AssetRef) => {
  if (!assetRef) return ""
  if (assetRef.type === "mux") {
    return `videos/${assetRef.snap_id}/${assetRef.id}`
  } else return `${assetRef.type}s/${assetRef.snap_id}/${assetRef.id}`
}

export function getBlockTypeCollection(blockRef: BlockRef) {
  const key = collectionMap[blockRef.type]
  return key ? db[key] : undefined
}

export async function deleteBlockContent(blockRef: BlockRef | undefined) {
  if (blockRef) {
    const collection = getBlockTypeCollection(blockRef)

    if (collection) {
      const doc = await get(collection, blockRef.id)
      if (doc) {
        let data = { meta: { ...doc.data.meta } }
        data.meta.deleted = true
        await updateWithMeta(collection, blockRef.id, data)
        return true
      }
    }
  }

  return false
}

export async function getAssetRecord(assetId: string): Promise<Doc<Asset>> {
  return get(db.assets, assetId)
    .then(res => {
      return res
    })
    .catch((err: firebase.FirebaseError) => {
      console.error(`Error (${err.code}) ${err.message}`)
      return null
    })
}

export async function getLibraryRecord(refId: string): Promise<Doc<Media>> {
  return get(db.library, refId)
    .then(res => {
      return res
    })
    .catch((err: firebase.FirebaseError) => {
      console.error(`Error (${err.code}) ${err.message}`)
      return null
    })
}

export async function getSnapRecord(snapId: string) {
  return get(db.snaps, snapId)
    .then(res => {
      return res
    })
    .catch((err: firebase.FirebaseError) => {
      console.error(`Error (${err.code}) ${err.message}`)
      return null
    })
}

export async function getScreenRecord(screenId: string) {
  return get(db.seq_screens, screenId)
    .then(res => {
      return res
    })
    .catch((err: firebase.FirebaseError) => {
      console.error(`Error (${err.code}) ${err.message}`)
      return null
    })
}

export async function updateScreenRecord(screenId: string, data: object) {
  await update(db.seq_screens, screenId, data).catch(
    (err: firebase.FirebaseError) => {
      console.error(`Error (${err.code}) ${err.message}`)
      return null
    }
  )
}

export async function getQtiInteractionRecord(qtiInteractionId: string) {
  return get(db.block_qti_interactions, qtiInteractionId)
    .then(res => {
      return res
    })
    .catch((err: firebase.FirebaseError) => {
      console.error(`Error (${err.code}) ${err.message}`)
      return null
    })
}

export async function getBlockImageRecord(blockImageId: string) {
  return get(db.block_images, blockImageId)
    .then(res => {
      return res
    })
    .catch((err: firebase.FirebaseError) => {
      console.error(`Error (${err.code}) ${err.message}`)
      return null
    })
}

export async function getBlockTextRecord(blockTextId: string) {
  return get(db.block_texts, blockTextId)
    .then(res => {
      return res
    })
    .catch((err: firebase.FirebaseError) => {
      console.error(`Error (${err.code}) ${err.message}`)
      return null
    })
}

export async function updateQtiInteractionRecord(
  qtiInteractionId: string,
  data: object
) {
  await update(db.block_qti_interactions, qtiInteractionId, data).catch(
    (err: firebase.FirebaseError) => {
      console.error(`Error (${err.code}) ${err.message}`)
      return null
    }
  )
}

export async function updateLibraryRecord(
  refId: string,
  media: Partial<Media>
) {
  await update(db.library, refId, media).catch(
    (err: firebase.FirebaseError) => {
      enqueueSnackbar(`Error: (${err.code}) ${err.message}`, {
        variant: "error"
      })
    }
  )
}

export async function updateBlockImageRecord(
  blockImageId: string,
  data: object
) {
  await update(db.block_images, blockImageId, data).catch(
    (err: firebase.FirebaseError) => {
      console.error(`Error (${err.code}) ${err.message}`)
      return null
    }
  )
}

export async function updateBlockTextRecord(
  blockImageId: string,
  data: object
) {
  await update(db.block_texts, blockImageId, data).catch(
    (err: firebase.FirebaseError) => {
      console.error(`Error (${err.code}) ${err.message}`)
      return null
    }
  )
}

export async function getInteractionRecord(interactionId: string) {
  return get(db.block_interactions, interactionId)
    .then(res => {
      return res
    })
    .catch((err: firebase.FirebaseError) => {
      console.error(`Error (${err.code}) ${err.message}`)
      return null
    })
}

export async function updateInteractionRecord(
  interactionId: string,
  data: object
) {
  await update(db.block_interactions, interactionId, data).catch(
    (err: firebase.FirebaseError) => {
      console.error(`Error (${err.code}) ${err.message}`)
      return null
    }
  )
}

export async function getBlockVideoRecord(blockVideoId: string) {
  return get(db.block_videos, blockVideoId)
    .then(res => {
      return res
    })
    .catch((err: firebase.FirebaseError) => {
      console.error(`Error (${err.code}) ${err.message}`)
      return null
    })
}

export async function updateBlockVideoRecord(
  blockVideoId: string,
  data: object
) {
  await update(db.block_videos, blockVideoId, data).catch(
    (err: firebase.FirebaseError) => {
      console.error(`Error (${err.code}) ${err.message}`)
      return null
    }
  )
}

export async function deleteBlockVideoRecord(blockVideoId: string) {
  await remove(db.block_videos, blockVideoId).catch(
    (err: firebase.FirebaseError) => {
      console.error(`Error (${err.code}) ${err.message}`)
      return null
    }
  )
}

export async function createLibraryRecord(
  type: MediaType,
  name: string,
  snapId: string,
  bucket: string,
  assets: Array<AssetRef>,
  meta: MetaType
) {
  const mediaAssets: Array<AssetRef> = assets as Array<AssetRef>

  const media = LibrarySchema.cast(
    {
      type: type,
      name: name,
      bucket: bucket,
      snap_id: snapId,
      assets: mediaAssets,
      meta: meta
    },
    validationOptions
  ) as Media

  let document = null
  await add(db.library, media)
    .then(doc => {
      document = doc
    })
    .catch((err: firebase.FirebaseError) => {
      enqueueSnackbar(`Error: (${err.code}) ${err.message}`, {
        variant: "error"
      })
    })
  return document
}

export const setLibraryRecord = async (
  id: string,
  type: MediaType,
  name: string,
  snapId: string,
  bucket: string,
  assets: Array<AssetRef>,
  meta: MetaType
) => {
  const mediaAssets: Array<AssetRef> = assets as Array<AssetRef>

  const media = LibrarySchema.cast(
    {
      type: type,
      name: name,
      bucket: bucket,
      snap_id: snapId,
      assets: mediaAssets,
      meta: meta
    },
    validationOptions
  )

  try {
    const doc = await set(ref(db.library, id), media)
    return doc
  } catch (error) {
    console.log(`Error: (${error.code}) ${error.message}`)
    return null
  }
}

export const getLibraryAsset = async (
  id: string
): Promise<LibraryAssetData> => {
  const doc = await get(ref(db.library, id))
  const url = await storage.ref(doc.data.bucket).getDownloadURL()
  return { id: doc.ref.id, media: doc.data, url }
}

export async function createPdfRecord(assets: Array<AssetRef>, meta: MetaType) {
  const pdf = BlockPdfSchema.cast(
    {
      assets: assets,
      meta: meta
    },
    validationOptions
  ) as BlockPdf

  return await add(db.block_pdfs, pdf)
}

export async function createPresentationRecord(
  assets: Array<AssetRef>,
  meta: MetaType
) {
  const presentation = BlockPresentationSchema.cast(
    {
      assets: assets,
      meta: meta
    },
    validationOptions
  ) as BlockPresentation

  return await add(db.block_presentations, presentation)
}

export async function createVideoRecord(
  assets: Array<AssetRef>,
  meta: MetaType
) {
  const video = BlockVideoSchema.cast(
    {
      type: "video",
      assets: assets,
      meta: meta
    },
    validationOptions
  ) as BlockVideo

  return await add(db.block_videos, video)
}

export async function createImageRecord(
  assets: Array<AssetRef>,
  meta: MetaType
) {
  const image = BlockImageSchema.cast(
    {
      type: "image",
      assets: assets,
      cropped: false,
      meta: meta
    },
    validationOptions
  ) as BlockImage

  return await add(db.block_images, image)
}

export async function createInteractionRecord(
  type: InteractionType,
  meta: MetaType,
  isAdaptive: boolean = false
) {
  const settings = SettingsSchema.cast(
    { settings: { shuffle: !!isAdaptive, choice_start: "A" } },
    validationOptions
  )

  console.log(`cast settings: ${JSON.stringify(settings)}`)
  const interaction = BlockInteractionSchema.cast(
    { type: type, questions: [{ settings }] },
    { ...validationOptions, context: { type: type } }
  ) as BlockInteraction
  const answer = AnswerSchema.cast(
    { type: "rate" },
    validationOptions
  ) as Answer

  if (type == "rate") {
    interaction.questions[0].question = "Rate your experience with this snap."
    interaction.questions[0].answers = [
      { ...answer },
      { ...answer },
      { ...answer },
      { ...answer }
    ]
  }

  return await add(db.block_interactions, interaction)
}

export const getSchemaInteractionType = (
  type: QtiInteractionType,
  getXmlType: boolean = false
) => {
  switch (type) {
    case "choice": {
      return InteractionTypes.choice
    }
    case "gapmatch": {
      return InteractionTypes.gapMatch
    }
    case "match": {
      return InteractionTypes.match
    }
    case "hotspot": {
      return InteractionTypes.hotspot
    }
    case "hottext": {
      return InteractionTypes.hottext
    }
    case "equationEditor": {
      if (getXmlType) {
        return InteractionTypes.customInteraction
      }
      return InteractionTypes.equationEditor
    }
    case "order": {
      return InteractionTypes.order
    }
    case "extendedtext": {
      return InteractionTypes.extendedText
    }
    case "inlineChoice": {
      return InteractionTypes.inlineChoice
    }
    case "textEntry": {
      return InteractionTypes.textEntry
    }
  }
}

type QtiContainerType = "lesson" | "exercise" | "practiceTest"

const QTI_CONTAINER_OVERRIDES: Partial<
  Record<
    QtiInteractionType,
    Partial<Record<QtiContainerType, (content: any) => void>>
  >
> = {
  extendedtext: {
    practiceTest: content => {
      content.attributes.neutralSetting = false
      content.content.enableRichText = false
      content.content.characterCount = "9550"
    }
  }
}

export async function createQtiInteractionRecord(
  type: QtiInteractionType,
  meta: MetaType,
  containerType?: QtiContainerType
) {
  const schemaInteractionType = getSchemaInteractionType(type)
  let interaction = BlockQtiInteractionSchema.cast(
    {
      type: type,
      meta: meta,
      answerFeedbacks: [],
      interactionFeedbacks: [],
      responseDeclaration: []
    },
    { ...validationOptions, context: { type: type } }
  )

  if (containerType) {
    const override = QTI_CONTAINER_OVERRIDES[type]?.[containerType]
    if (override) {
      override(interaction.content)
    }
  }

  // Set the answer responseDeclaraction identifier to be the interaction responseIdentifier so they can match up
  interaction.responseDeclaration = [
    INITIAL_RESPONSE_DECLARATION[schemaInteractionType](
      interaction.content.attributes.responseIdentifier
    )
  ]

  return await add(db.block_qti_interactions, interaction)
}

export async function createTextRecord(type: TextType, meta: MetaType) {
  const textBlock = BlockTextSchema.cast(
    {
      type: type,
      meta: meta
    },
    validationOptions
  ) as BlockText

  return await add(db.block_texts, textBlock)
}

export async function createAssetRecord(
  type: AssetType,
  name: string,
  meta: MetaType,
  altText?: string
) {
  const asset = AssetSchema.cast(
    {
      type: type,
      name: name,
      meta: meta,
      altText: altText
    },
    validationOptions
  ) as Asset

  let doc: Doc<Asset> = {} as Doc<Asset>

  await add(db.assets, asset)
    .then(newDoc => (doc = newDoc))
    .catch((err: firebase.FirebaseError) => {
      enqueueSnackbar(`Error: (${err.code}) ${err.message}`, {
        variant: "error"
      })
    })

  return doc
}

export async function updateAssetRecord(refId: string, data: Partial<Asset>) {
  await update(db.assets, refId, data).catch((err: firebase.FirebaseError) => {
    enqueueSnackbar(`Error: (${err.code}) ${err.message}`, {
      variant: "error"
    })
  })
}

export async function createEmptySequenceRef(
  name: string,
  type: SequenceType,
  meta: MetaType,
  layout?: LayoutType,
  mobileLayout?: MobileLayout,
  description?: string,
  snapId?: string
): Promise<SeqRef> {
  const seqRef = SeqRefSchema.cast(
    {
      type: type,
      id: ""
    },
    validationOptions
  ) as SeqRef

  if (type == "screen" && layout) {
    const emptyBlock = BlockRefSchema.cast({}, validationOptions)
    const screen = SeqScreenSchema.cast(
      {
        name: name,
        layout: layout,
        mobileLayout: mobileLayout,
        left: [emptyBlock],
        right: [emptyBlock],
        meta: meta
      },
      validationOptions
    )

    const screenDoc = await add(db.seq_screens, screen).catch(
      (err: firebase.FirebaseError) => {
        enqueueSnackbar(`Error: (${err.code}) ${err.message}`, {
          variant: "error"
        })
      }
    )

    if (screenDoc) {
      seqRef.id = screenDoc.ref.id
    }
  }

  if (type == "qtiScreen" && layout && snapId) {
    const emptyBlock = BlockRefSchema.cast({}, validationOptions)
    const screen = SeqQtiScreenSchema.cast(
      {
        name: name,
        layout: layout,
        mobileLayout: mobileLayout,
        published: false,
        left: [emptyBlock],
        right: [emptyBlock],
        meta: meta,
        snapId
      },
      validationOptions
    )

    const screenDoc = await add(db.seq_qti_screens, screen).catch(
      (err: firebase.FirebaseError) => {
        enqueueSnackbar(`Error: (${err.code}) ${err.message}`, {
          variant: "error"
        })
      }
    )

    if (screenDoc) {
      seqRef.id = screenDoc.ref.id
    }
  }

  if (type == "passage" && layout && snapId) {
    const emptyBlock = BlockRefSchema.cast({}, validationOptions)
    const passage = SeqPassageSchema.cast(
      {
        name: name,
        layout: "Centered Column",
        mobileLayout: mobileLayout,
        published: false,
        left: [emptyBlock],
        meta: meta,
        snapId
      },
      validationOptions
    )

    const passageDoc = await add(db.seq_passages, passage).catch(
      (err: firebase.FirebaseError) => {
        enqueueSnackbar(`Error: (${err.code}) ${err.message}`, {
          variant: "error"
        })
      }
    )

    if (passageDoc) {
      seqRef.id = passageDoc.ref.id
    }
  }

  if (type == "exercise" && layout) {
    const emptyBlock = BlockRefSchema.cast({}, validationOptions)
    const exercise = SeqExerciseSchema.cast(
      {
        name: name,
        layout: layout,
        mobileLayout: mobileLayout,
        left: [emptyBlock],
        right: [emptyBlock],
        meta: meta
      },
      validationOptions
    )

    const exerciseDoc = await add(db.seq_exercises, exercise).catch(
      (err: firebase.FirebaseError) => {
        enqueueSnackbar(`Error: (${err.code}) ${err.message}`, {
          variant: "error"
        })
      }
    )

    if (exerciseDoc) {
      seqRef.id = exerciseDoc.ref.id
    }
  }

  if (type == "assessment" && layout) {
    const emptyBlock = BlockRefSchema.cast({}, validationOptions)
    const assessment = SeqAssessmentSchema.cast(
      {
        name: name,
        description: description,
        layout: layout,
        mobileLayout: mobileLayout,
        left: [emptyBlock],
        right: [emptyBlock],
        meta: meta
      },
      validationOptions
    )

    const assessmentDoc = await add(db.seq_assessments, assessment).catch(
      (err: firebase.FirebaseError) => {
        enqueueSnackbar(`Error: (${err.code}) ${err.message}`, {
          variant: "error"
        })
      }
    )

    if (assessmentDoc) {
      seqRef.id = assessmentDoc.ref.id
    }
  }

  return seqRef
}

const SnapTypeCollectionMap: Record<SequenceType, CollectionNames> = {
  screen: "seq_screens",
  exercise: "seq_exercises",
  assessment: "seq_assessments",
  config: "seq_configs",
  logic: "seq_logics",
  qtiScreen: "seq_qti_screens",
  passage: "seq_passages"
}

type SnapTypeConfig = {
  seqType: SequenceType
  seqCollection:
    | Collection<SeqScreen>
    | Collection<SeqExercise>
    | Collection<SeqAssessment>
    | Collection<SeqQtiScreen>
    | Collection<SeqPassage>
  seqTemplate:
    | SeqScreen
    | SeqExercise
    | SeqAssessment
    | SeqQtiScreen
    | SeqPassage
}

const getSeqData = (
  type: SnapType,
  meta: MetaType,
  isAdaptive?: boolean,
  screenBankType?: ScreenBankEntityTypes
) => {
  const emptyBlock = BlockRefSchema.cast({}, validationOptions)

  const screen = SeqScreenSchema.cast(
    {
      name: "Unnamed",
      left: [emptyBlock],
      right: [emptyBlock],
      meta: meta
    },
    validationOptions
  ) as SeqScreen

  const qtiScreen =
    screenBankType === "question"
      ? (SeqQtiScreenSchema.cast(
          {
            name: name,
            published: false,
            left: [emptyBlock],
            right: [emptyBlock],
            meta: meta,
            type: screenBankType
          },
          validationOptions
        ) as SeqQtiScreen)
      : null

  const passage =
    screenBankType === "passage"
      ? (SeqPassageSchema.cast(
          {
            name: name,
            published: false,
            left: [emptyBlock],
            meta: meta
          },
          validationOptions
        ) as SeqPassage)
      : null

  const exercise = SeqExerciseSchema.cast(
    {
      name: "Unnamed",
      layout: "Centered Column",
      left: [emptyBlock],
      right: [emptyBlock],
      meta: meta
    },
    validationOptions
  ) as SeqExercise

  const assessment = isAdaptive
    ? (SeqAssessmentSchema.cast(
        {
          name: "Item 1",
          description: "Item 1",
          layout: "Centered Column",
          left: [emptyBlock],
          right: [emptyBlock],
          meta: meta
        },
        validationOptions
      ) as SeqAssessment)
    : (SeqAssessmentSchema.cast(
        {
          name: "Question 1",
          description: "Question 1",
          layout: "Centered Column",
          left: [emptyBlock],
          right: [emptyBlock],
          meta: meta
        },
        validationOptions
      ) as SeqAssessment)

  const ScreenBankConfig: SnapTypeConfig = {
    seqType: screenBankType === "question" ? "qtiScreen" : "passage",
    seqCollection:
      screenBankType === "question" ? db.seq_qti_screens : db.seq_passages,
    seqTemplate: screenBankType === "question" ? qtiScreen : passage
  }

  const SnapTypeMap: Record<SnapType, SnapTypeConfig> = {
    snap: !!screenBankType
      ? ScreenBankConfig
      : {
          seqType: "screen",
          seqCollection: db.seq_screens,
          seqTemplate: screen
        },
    element: {
      seqType: "exercise",
      seqCollection: db.seq_exercises,
      seqTemplate: exercise
    },
    assessment: {
      seqType: "assessment",
      seqCollection: db.seq_assessments,
      seqTemplate: assessment
    }
  }

  const seqType = SnapTypeMap[type].seqType
  const seqCollection = SnapTypeMap[type].seqCollection
  const seqTemplate = SnapTypeMap[type].seqTemplate

  return { seqType, seqCollection, seqTemplate }
}

export async function createEmptySnapRecord(
  name: string,
  options: {
    type: SnapType
    meta: MetaType
    program?: Program | "STAARv2"
    subject?: string
    isAdaptive?: boolean
    contentData?: { contentType: string; contentId: string }
    screenBankType?: ScreenBankEntityTypes
  }
) {
  const {
    type,
    meta,
    program,
    subject,
    isAdaptive,
    contentData,
    screenBankType
  } = options
  //let doc: Doc<Snap> = {} as Doc<Snap>

  const { seqCollection, seqTemplate, seqType } = getSeqData(
    type,
    meta,
    isAdaptive,
    screenBankType
  )

  const seqDoc = await add(seqCollection, seqTemplate as any).catch(
    (err: firebase.FirebaseError) => {
      enqueueSnackbar(`Error: (${err.code}) ${err.message}`, {
        variant: "error"
      })
    }
  )

  if (seqDoc) {
    const seqRef = SeqRefSchema.cast({ id: seqDoc.ref.id, type: seqType })

    let assessmentBranch: any = {
      branches: { "Question Group 1": [seqRef] },
      branchOrder: ["Question Group 1"]
    }

    if (isAdaptive !== undefined && isAdaptive) {
      assessmentBranch = {
        branches: { "Level 1": [seqRef] },
        branchOrder: ["Level 1"]
      }
    }

    const snap = SnapSchema.cast(
      {
        name: name,
        type: type,
        ...(seqType === "assessment"
          ? {
              ...assessmentBranch
            }
          : {
              branches: { Main: [seqRef] },
              branchOrder: ["Main"]
            }),
        meta: meta,
        ...(program !== undefined && { program: program }),
        ...(subject !== undefined && { subject: subject }),
        ...(isAdaptive !== undefined && { adaptive: isAdaptive }),
        ...(contentData ?? {})
      },
      validationOptions
    ) as Snap

    try {
      const doc = await add(db.snaps, snap)
      if (screenBankType) {
        const collection = ScreenTypeToCollectionMapping[screenBankType]
        await update(db[collection], seqDoc.ref.id, { snapId: doc.ref.id })
      }

      return doc
    } catch (err) {
      enqueueSnackbar(`Error: (${err.code}) ${err.message}`, {
        variant: "error"
      })

      // clean up the seq
      if (seqDoc?.ref?.id) {
        remove(seqCollection, seqDoc.ref.id)
      }
    }
  }
}

export async function createEmptySnapCourseRecord(
  name: string,
  topics: TopicMap,
  meta: MetaType
) {
  let doc: Doc<SnapCourse> = {} as Doc<SnapCourse>
  let newTopics: TopicMap = { Uncategorized: [] }
  if (Object.keys(topics).length) {
    newTopics = topics
  }
  const snapCourse = SnapCourseSchema.cast(
    {
      name: name,
      topics: newTopics,
      published: false,
      meta: meta
    },
    validationOptions
  ) as SnapCourse

  await add(db.snap_courses, snapCourse)
    .then(newDoc => (doc = newDoc))
    .catch((err: firebase.FirebaseError) => {
      enqueueSnackbar(`Error: (${err.code}) ${err.message}`, {
        variant: "error"
      })
    })

  return doc
}

export async function updateWithMeta<Model>(
  collection: Collection<Model>,
  id: string,
  data: any
): Promise<void> {
  const currentUser = firebase.auth().currentUser
  const uid = currentUser?.uid || ""

  let saveData = cloneDeep(data)
  if (saveData?.meta) {
    saveData.meta.updated = firebase.firestore.Timestamp.now()
  } else {
    const metaData = (await get(collection, id)) as Doc<any>
    if (metaData && metaData.data) {
      saveData.meta = {
        ...metaData.data.meta,
        updated: firebase.firestore.Timestamp.now(),
        last_updated_by: uid
      }
    }
  }

  return update(collection, id, saveData as Model)
}

export async function addWordBank(data: WordBank) {
  let doc = await add(db.word_banks, data)
  return doc
}

export async function updateVideoSettings(videoSettings: VideoSettings) {
  const currentUser = firebase.auth().currentUser
  const uid = currentUser?.uid || ""
  updateWithMeta(db.users, uid, {
    preferences: { videoSettings: videoSettings }
  })
}

export const createSnapcourseRecord = async (
  programCourses: SnapCourseProgramCourseAssociationInput[],
  name: string,
  userId: string,
  snapType: SnapType,
  type: SnapCourseType
) => {
  const meta = createMeta(userId)
  const { seqType, seqCollection, seqTemplate } = getSeqData(snapType, meta)

  const seqDoc = await add(seqCollection, seqTemplate as any).catch(
    (err: firebase.FirebaseError) => {
      enqueueSnackbar(`Error: (${err.code}) ${err.message}`, {
        variant: "error"
      })
    }
  )

  if (!seqDoc) return Promise.reject(null)

  const seqRef = SeqRefSchema.cast({ id: seqDoc.ref.id, type: seqType })
  const program = programCourses?.[0]?.program ?? null
  const subject = programCourses?.[0]?.course ?? null

  const inputParam: SnapCourseCreateInput = {
    programCourses,
    type,
    snapDoc: {
      name: name,
      description: name,
      type: snapType,
      kind: "snaps",
      branches: { Main: [seqRef] },
      branchOrder: ["Main"],
      meta,
      adaptive: false,
      version: 1,
      ...(type === "MASTERY_LESSON" && { contentType: "MASTERY" }),
      ...(program && { program: program }),
      ...(subject && { subject: subject })
    }
  }

  return createSnapCourse(inputParam)
}

export const getQtiScreensByType = async (
  type: "question" | "passage"
): Promise<Doc<SeqQtiScreen>[]> => {
  const collection = ScreenTypeToCollectionMapping[type]

  if (!collection) return []

  try {
    const result = await query(db[collection], [
      where(["meta", "deleted"], "==", false)
    ])

    return result as Doc<SeqQtiScreen>[]
  } catch (e) {
    enqueueSnackbar(`Error: (${e.code}) ${e.message}`, { variant: "error" })
  }
}

export const deleteEntityFromScreenBank = async (
  id: string,
  collection: "seq_qti_screens" | "seq_passages"
) => {
  try {
    const screen = await get(db[collection], id)
    const snapId = screen?.data?.snapId ?? ""
    const snap = await get(db.snaps, snapId)

    const originalScreenMeta = screen.data.meta
    const originalSnapMeta = snap.data.meta
    await update(db[collection], id, {
      meta: { ...originalScreenMeta, deleted: true }
    })
    await update(db.snaps, snapId, {
      meta: { ...originalSnapMeta, deleted: true }
    })
  } catch (e) {
    enqueueSnackbar(`Error: (${e.code}) ${e.message}`, { variant: "error" })
  }
}

// TODO: need to uncommented out commented code after changes on b/e side connected with getting item method
export const updateScreenBankItem = async (
  data: Partial<SeqQtiScreen>,
  id: string,
  collection: "seq_qti_screens" | "seq_passages"
  // data: ScreenBankItemPropertiesUpdateInput | PassagePropertiesUpdateInput,
) => {
  try {
    // if (collection === "seq_qti_screens") {
    //   await updateScreenBankQuestionConfig(data)
    // } else {
    //   await updateScreenBankPassageConfig(data)
    // }

    const qtiScreenDb =
      collection === "seq_qti_screens" ? db.seq_qti_screens : db.seq_passages

    const screen = await get(qtiScreenDb, id)

    await update(qtiScreenDb, id, { ...screen.data, ...data })
  } catch (e) {
    enqueueSnackbar(`Error: (${e.code}) ${e.message}`, { variant: "error" })
  }
}

export const updatePassageItem = async (
  data: Partial<SeqPassage>,
  id: string
) => {
  try {
    const passage = await get(db.seq_passages, id)
    await update(db.seq_passages, id, { ...passage.data, ...data })
  } catch (e) {
    enqueueSnackbar(`Error: (${e.code}) ${e.message}`, { variant: "error" })
  }
}

export const findSeqAssessmentByQuestionId = async (
  id: string
): Promise<Doc<SeqAssessment>[]> => {
  try {
    const [leftDocs, rightDocs] = await Promise.all([
      query(db.seq_assessments, [
        where("left", "array-contains", { id, type: "interaction" })
      ]),
      query(db.seq_assessments, [
        where("right", "array-contains", { id, type: "interaction" })
      ])
    ])
    const merged = [...leftDocs, ...rightDocs]

    return Array.from(new Map(merged.map(d => [d.ref.id, d])).values())
  } catch (error) {
    console.warn("Error (findSeqAssessmentByQuestionId):", error)

    return null
  }
}
